import { useCart } from "@/context/cart-context";
import { useLocale } from "@/context/locale-context";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { X, ShoppingBag, Trash2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export function CartDrawer() {
  const { items, isOpen, closeCart, removeItem, subtotalUSD } = useCart();
  const { t, formatPrice } = useLocale();

  return (
    <>
      {isOpen && <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={closeCart} data-testid="cart-overlay" />}
      <aside className={`fixed top-0 right-0 z-50 h-full w-full max-w-md bg-background shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"}`} data-testid="cart-drawer">
        <div className="flex items-center justify-between px-6 py-5 border-b">
          <div className="flex items-center gap-3">
            <ShoppingBag className="w-5 h-5" />
            <h2 className="font-serif text-xl">{t.cart.title}</h2>
            {items.length > 0 && <span className="text-xs uppercase tracking-widest text-muted-foreground">({items.length})</span>}
          </div>
          <button onClick={closeCart} className="rounded-none p-1 text-muted-foreground hover:text-foreground transition-colors" data-testid="close-cart-btn"><X className="w-5 h-5" /></button>
        </div>
        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
            <ShoppingBag className="w-12 h-12 text-muted-foreground/30 mb-6" />
            <p className="font-serif text-xl mb-2">{t.cart.empty}</p>
            <p className="text-muted-foreground text-sm mb-8">{t.cart.emptySubtitle}</p>
            <Link to="/gallery" onClick={closeCart}><Button variant="outline" className="rounded-none uppercase tracking-widest text-xs">{t.cart.browseCatalog}</Button></Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto py-4">
              {items.map(({ painting }) => {
                const price = painting.onSale && painting.salePrice ? painting.salePrice : painting.price;
                return (
                  <div key={painting.id} className="flex gap-4 px-6 py-4 border-b last:border-0">
                    <div className="w-20 h-24 bg-muted shrink-0 overflow-hidden">
                      {painting.imageUrl ? <img src={painting.imageUrl} alt={painting.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center bg-muted/50" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-serif text-base leading-tight mb-1 truncate">{painting.title}</h3>
                      {painting.categoryName && <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{painting.categoryName}</p>}
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{formatPrice(price)}</span>
                        {painting.onSale && painting.salePrice && <span className="text-xs text-muted-foreground line-through">{formatPrice(painting.price)}</span>}
                      </div>
                    </div>
                    <button onClick={() => removeItem(painting.id)} className="text-muted-foreground hover:text-destructive transition-colors self-start mt-1" aria-label={t.cart.remove} data-testid={`remove-cart-${painting.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                );
              })}
            </div>
            <div className="border-t px-6 py-6 space-y-4">
              <div className="flex justify-between text-sm text-muted-foreground"><span className="uppercase tracking-widest">{t.cart.subtotal}</span><span>{formatPrice(subtotalUSD)}</span></div>
              <div className="flex justify-between text-sm text-muted-foreground"><span className="uppercase tracking-widest">{t.cart.shipping}</span><span className="italic">{t.cart.shippingNote}</span></div>
              <Separator />
              <div className="flex justify-between font-medium"><span className="uppercase tracking-widest">{t.cart.total}</span><span className="font-serif text-lg">{formatPrice(subtotalUSD)}</span></div>
              <Link to="/checkout" onClick={closeCart}><Button className="w-full rounded-none h-12 uppercase tracking-widest text-sm font-medium" data-testid="checkout-btn">{t.cart.checkout} <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
            </div>
          </>
        )}
      </aside>
    </>
  );
}
